import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Heading,
  Select,
  FormControl,
  FormLabel,
  Input,
  Divider,
  useToast,
  Badge,
  IconButton,
  Tooltip,
  useColorModeValue,
  Grid,
  GridItem,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, EditIcon, SettingsIcon, TimeIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useApi } from '@/contexts/ApiContext';
import { useAuth } from '@/contexts/AuthContext';
import { decodeJWT } from '@/utils/jwt';
import { useContacts } from '@/hooks/useContacts';
import { CordeCommonModelsDtoContactContact } from '@/api/generated/v1/api';

interface NotificationEventSettings {
  event_type: string;
  contact_preferences: {
    contact_type: string;
    mode: 'disabled' | 'primary' | 'all'; // Delivery mode
  }[];
  quiet_hours_respected: boolean;
  priority_override?: 'low' | 'normal' | 'high' | 'urgent';
}

interface UserNotificationSettings {
  quiet_hours: {
    enabled: boolean;
    start_time: string; // HH:MM format
    end_time: string; // HH:MM format
    timezone: string;
  };
  emergency_contacts: string[]; // Contact IDs that bypass quiet hours
  global_enabled: boolean;
  event_settings: NotificationEventSettings[];
}

const EVENT_TYPES = [
  'balance_low',
  'balance_updated',
  'machine_error',
  'machine_maintenance',
  'team_invitation',
  'security_alert',
  'system_maintenance',
  'payment_received',
  'payment_failed',
  'profile_updated',
];

const CONTACT_TYPES = [
  'email',
  'telegram',
  'whatsapp',
  'sms',
  'discord',
  'slack',
];

export const NotificationUserSettings: React.FC = () => {
  const { t } = useTranslation();
  const api = useApi();
  const { accessToken } = useAuth();
  const toast = useToast();
  
  // State
  const [settings, setSettings] = useState<UserNotificationSettings>({
    quiet_hours: {
      enabled: false,
      start_time: '22:00',
      end_time: '08:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    emergency_contacts: [],
    global_enabled: true,
    event_settings: EVENT_TYPES.map(event => ({
      event_type: event,
      contact_preferences: CONTACT_TYPES.map(type => ({
        contact_type: type,
        mode: 'primary' as const,
      })),
      quiet_hours_respected: true,
    })),
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState<CordeCommonModelsDtoContactContact[]>([]);

  // Get user's contacts
  const { listContacts } = useContacts();

  // Theme colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Load user contacts and settings
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Get user ID from token
        const userId = accessToken ? decodeJWT(accessToken)?.sub : null;
        
        // Load user contacts
        if (userId) {
          const userContacts = await listContacts('userprofile', userId);
          setContacts(userContacts);
        }
        
        // Load notification settings
        // TODO: Implement API call to load user notification settings
        // const response = await api.NotificationsApi.getUserNotificationSettings(userId);
        // setSettings(response.data.settings);
        
      } catch (error) {
        console.error('Failed to load notification settings:', error);
        toast({
          title: t('common.error'),
          description: t('notifications.loadSettingsError'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [accessToken]);

  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // Get user ID from token
      const userId = accessToken ? decodeJWT(accessToken)?.sub : null;
      if (!userId) {
        throw new Error('User not authenticated');
      }
      
      // TODO: Implement API call to save settings
      // await api.NotificationsApi.updateUserNotificationSettings(userId, settings);
      
      toast({
        title: t('common.success'),
        description: t('notifications.settingsSaved'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.saveSettingsError'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  // Update contact delivery mode
  const updateContactDeliveryMode = (eventType: string, contactType: string, mode: 'disabled' | 'primary' | 'all') => {
    setSettings(prev => ({
      ...prev,
      event_settings: prev.event_settings.map(event => 
        event.event_type === eventType 
          ? {
              ...event,
              contact_preferences: event.contact_preferences.map(contact => 
                contact.contact_type === contactType 
                  ? { ...contact, mode }
                  : contact
              )
            }
          : event
      )
    }));
  };

  // Get contacts by type
  const getContactsByType = (type: string) => {
    return contacts.filter(contact => contact.type === type);
  };

  // Get primary contact by type
  const getPrimaryContact = (type: string) => {
    return contacts.find(contact => contact.type === type && contact.is_primary);
  };

  if (loading) {
    return (
      <Box>
        <Flex justify="center" align="center" h="200px">
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Global Settings */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <HStack justify="space-between">
            <Heading size="md">{t('notifications.globalSettings')}</Heading>
            <Switch
              isChecked={settings.global_enabled}
              onChange={(e) => setSettings(prev => ({ ...prev, global_enabled: e.target.checked }))}
              colorScheme="green"
            />
          </HStack>
        </CardHeader>
        <CardBody>
          <Text color="gray.600" fontSize="sm">
            {t('notifications.globalSettingsDescription')}
          </Text>
        </CardBody>
      </Card>

      {/* Quiet Hours */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <HStack justify="space-between">
            <HStack>
              <TimeIcon />
              <Heading size="md">{t('notifications.quietHours')}</Heading>
            </HStack>
            <Switch
              isChecked={settings.quiet_hours.enabled}
              onChange={(e) => setSettings(prev => ({ 
                ...prev, 
                quiet_hours: { ...prev.quiet_hours, enabled: e.target.checked }
              }))}
              colorScheme="purple"
            />
          </HStack>
        </CardHeader>
        {settings.quiet_hours.enabled && (
          <CardBody>
            <Grid templateColumns="repeat(2, 1fr)" gap={4}>
              <GridItem>
                <FormControl>
                  <FormLabel>{t('notifications.startTime')}</FormLabel>
                  <Input
                    type="time"
                    value={settings.quiet_hours.start_time}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      quiet_hours: { ...prev.quiet_hours, start_time: e.target.value }
                    }))}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>{t('notifications.endTime')}</FormLabel>
                  <Input
                    type="time"
                    value={settings.quiet_hours.end_time}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      quiet_hours: { ...prev.quiet_hours, end_time: e.target.value }
                    }))}
                  />
                </FormControl>
              </GridItem>
            </Grid>
            <Box mt={4}>
              <FormControl>
                <FormLabel>{t('notifications.timezone')}</FormLabel>
                <Select
                  value={settings.quiet_hours.timezone}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    quiet_hours: { ...prev.quiet_hours, timezone: e.target.value }
                  }))}
                >
                  <option value="UTC">UTC</option>
                  <option value="Europe/Moscow">Europe/Moscow</option>
                  <option value="America/New_York">America/New_York</option>
                  <option value="Asia/Tokyo">Asia/Tokyo</option>
                </Select>
              </FormControl>
            </Box>
          </CardBody>
        )}
      </Card>

      {/* Notification Matrix */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <HStack>
            <SettingsIcon />
            <Heading size="md">{t('notifications.notificationMatrix')}</Heading>
          </HStack>
        </CardHeader>
        <CardBody>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {t('notifications.matrixDescription')}
          </Text>
          
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>{t('notifications.eventType')}</Th>
                  {CONTACT_TYPES.map(type => (
                    <Th key={type} textAlign="center">
                      <VStack spacing={1}>
                        <Text>{type.toUpperCase()}</Text>
                        <Badge variant="outline" fontSize="xs">
                          {getPrimaryContact(type) ? t('notifications.hasPrimary') : t('notifications.noPrimary')}
                        </Badge>
                      </VStack>
                    </Th>
                  ))}
                  <Th textAlign="center">{t('notifications.quietHours')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {settings.event_settings.map(eventSetting => (
                  <Tr key={eventSetting.event_type} _hover={{ bg: hoverBg }}>
                    <Td fontWeight="medium">
                      <VStack align="start" spacing={1}>
                        <Text>{t(`notifications.events.${eventSetting.event_type}`)}</Text>
                        <Badge size="sm" colorScheme="blue">
                          {eventSetting.priority_override || 'normal'}
                        </Badge>
                      </VStack>
                    </Td>
                    {CONTACT_TYPES.map(contactType => {
                      const preference = eventSetting.contact_preferences.find(
                        p => p.contact_type === contactType
                      );
                      const contactsOfType = getContactsByType(contactType);
                      const primaryContact = getPrimaryContact(contactType);
                      
                      // Determine default option based on primary contact availability
                      const defaultMode = primaryContact ? 'primary' : 'disabled';
                      
                      return (
                        <Td key={contactType} textAlign="center">
                          <Select
                            size="sm"
                            value={preference?.mode || defaultMode}
                            onChange={(e) => updateContactDeliveryMode(
                              eventSetting.event_type,
                              contactType,
                              e.target.value as 'disabled' | 'primary' | 'all'
                            )}
                          >
                            <option value="disabled">{t('notifications.contactDeliveryOptions.disabled')}</option>
                            <option value="primary" disabled={!primaryContact}>
                              {t('notifications.contactDeliveryOptions.primary')}
                            </option>
                            <option value="all" disabled={contactsOfType.length === 0}>
                              {t('notifications.contactDeliveryOptions.all')}
                            </option>
                          </Select>
                        </Td>
                      );
                    })}
                    <Td textAlign="center">
                      <Switch
                        isChecked={eventSetting.quiet_hours_respected}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          event_settings: prev.event_settings.map(event => 
                            event.event_type === eventSetting.event_type 
                              ? { ...event, quiet_hours_respected: e.target.checked }
                              : event
                          )
                        }))}
                        size="sm"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {/* Emergency Contacts */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">{t('notifications.emergencyContacts')}</Heading>
        </CardHeader>
        <CardBody>
          <Text color="gray.600" fontSize="sm" mb={4}>
            {t('notifications.emergencyContactsDescription')}
          </Text>
          
          {settings.emergency_contacts.length === 0 ? (
            <Alert status="info">
              <AlertIcon />
              {t('notifications.noEmergencyContacts')}
            </Alert>
          ) : (
            <VStack spacing={2} align="stretch">
              {settings.emergency_contacts.map(contactId => {
                const contact = contacts.find(c => c.id === contactId);
                return (
                  <HStack key={contactId} justify="space-between" p={2} borderRadius="md" bg={hoverBg}>
                    <HStack>
                      <Badge colorScheme="red">Emergency</Badge>
                      <Text>{contact?.value || contactId}</Text>
                      <Badge variant="outline">{contact?.type}</Badge>
                    </HStack>
                    <IconButton
                      aria-label={t('common.remove')}
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => setSettings(prev => ({
                        ...prev,
                        emergency_contacts: prev.emergency_contacts.filter(id => id !== contactId)
                      }))}
                    />
                  </HStack>
                );
              })}
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Save Button */}
      <Flex justify="end">
        <Button
          colorScheme="blue"
          size="lg"
          onClick={handleSaveSettings}
          isLoading={saving}
          loadingText={t('common.saving')}
        >
          {t('common.save')}
        </Button>
      </Flex>
    </VStack>
  );
}; 